import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { Mail, Heart, ExternalLink, Cookie, HelpCircle, PawPrint, Instagram, Facebook } from "lucide-react";
import { FAQModalLazy } from "@/components/FAQModalLazy";
import { Brand, BRAND_NAME } from "./Brand";

export const Footer = () => {
  const { isAdmin } = useAuth();
  const { resetConsent } = useCookieConsent();

  const linkBase =
    "group inline-flex items-center gap-1.5 text-[11px] sm:text-xs text-muted-foreground hover:text-foreground transition-colors";

  return (
    <footer className="relative mt-8 sm:mt-12 overflow-hidden">
      {/* Soft gradient backdrop */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/[0.04] via-background to-background pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary/[0.06] blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-primary/[0.05] blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 py-8 sm:py-12 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 sm:gap-10">
          {/* Brand */}
          <div className="md:col-span-4 space-y-4">
            <Brand showIcon size="md" />
            <p className="text-[11px] sm:text-xs text-muted-foreground leading-relaxed max-w-sm">
              Pomagamy łączyć ludzi ze schroniskami, fundacjami i organizacjami pomagającymi zwierzętom w całej Polsce.
            </p>

            <div className="flex flex-wrap gap-2 pt-1">
              <FAQModalLazy>
                <button className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-primary bg-primary/10 hover:bg-primary/15 px-3 py-1.5 rounded-full transition-colors">
                  <HelpCircle className="w-3.5 h-3.5" />
                  FAQ
                </button>
              </FAQModalLazy>
              <Link
                to="/dla-schronisk"
                className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-primary bg-primary/10 hover:bg-primary/15 px-3 py-1.5 rounded-full transition-colors"
              >
                <PawPrint className="w-3.5 h-3.5" />
                Dla schronisk
              </Link>
              <Link
                to="/o-projekcie"
                className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-medium text-primary bg-primary/10 hover:bg-primary/15 px-3 py-1.5 rounded-full transition-colors"
              >
                <Heart className="w-3.5 h-3.5" />
                O projekcie
              </Link>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <a
                href="https://www.instagram.com/gdzieschronisko.pl"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-background/70 backdrop-blur border border-border/60 hover:border-primary/40 hover:bg-primary/5 flex items-center justify-center transition-all hover:-translate-y-0.5 shadow-sm"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4 text-primary" />
              </a>
              <a
                href="https://www.facebook.com/gdzieschronisko"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-background/70 backdrop-blur border border-border/60 hover:border-primary/40 hover:bg-primary/5 flex items-center justify-center transition-all hover:-translate-y-0.5 shadow-sm"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4 text-primary" />
              </a>
            </div>
          </div>

          {/* Contact */}
          <div className="md:col-span-3 space-y-3">
            <h3 className="font-semibold text-[11px] sm:text-xs uppercase tracking-wider text-foreground/80">
              Kontakt
            </h3>
            <p className="text-[11px] sm:text-xs text-muted-foreground leading-relaxed">
              {BRAND_NAME} to oddolna, niezależna inicjatywa informacyjna. Obecność na stronie jest całkowicie darmowa.
            </p>
            <a
              href="mailto:kontakt@gdzieschronisko.pl"
              className="inline-flex items-center gap-2 text-[11px] sm:text-xs text-foreground/80 hover:text-primary transition-colors group"
            >
              <span className="w-7 h-7 rounded-full bg-primary/10 group-hover:bg-primary/15 flex items-center justify-center transition-colors">
                <Mail className="w-3.5 h-3.5 text-primary" />
              </span>
              kontakt@gdzieschronisko.pl
            </a>
          </div>

          {/* Resources */}
          <div className="md:col-span-5 space-y-3">
            <h3 className="font-semibold text-[11px] sm:text-xs uppercase tracking-wider text-foreground/80">
              Przydatne linki
            </h3>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
              {[
                { href: "https://napaluchu.waw.pl/edukacja/co-zrobic-jak-znajdziesz-zwierze/", label: "Znalazłeś zwierzę?" },
                { href: "https://fundacjazwierz.org/jak-zglosic-znecanie-sie-nad-zwierzakiem/", label: "Zgłoś znęcanie" },
                { href: "https://www.safe-animal.eu/pl/gdzie-zglosic-zaginiecie-psa", label: "Zaginiony pies/kot" },
                { href: "https://pupilmed.pl/blog/pierwsza-pomoc-u-psa", label: "Pierwsza pomoc" },
                { href: "https://www.lasy.gov.pl/pl/informacje/infografiki/co-zrobic-gdy-spotkasz-w-lesie", label: "Dzikie zwierzęta" },
                { href: "https://toz.pl/jak-pomoc/", label: "Jak pomagać? (TOZ)" },
                { href: "https://www.wetgiw.gov.pl/", label: "Inspekcja Weterynaryjna" },
              ].map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkBase}
                  >
                    <span className="truncate">{link.label}</span>
                    <ExternalLink className="w-2.5 h-2.5 flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 sm:mt-10 pt-5 sm:pt-6 border-t border-border/60 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 text-[11px] sm:text-xs text-muted-foreground">
          <div className="flex items-center gap-3 sm:gap-5 flex-wrap justify-center sm:justify-start">
            <p>© 2026 {BRAND_NAME}</p>
            <Link to="/regulamin" className="hover:text-foreground transition-colors">
              Regulamin
            </Link>
            <Link to="/polityka-prywatnosci" className="hover:text-foreground transition-colors">
              Prywatność
            </Link>
            <button
              onClick={resetConsent}
              className="hover:text-foreground transition-colors inline-flex items-center gap-1"
            >
              <Cookie className="w-3.5 h-3.5" />
              Cookies
            </button>
          </div>
          <p className="inline-flex items-center gap-1.5">
            Stworzone z <Heart className="w-3.5 h-3.5 text-destructive fill-destructive" /> dla zwierząt
          </p>
        </div>
      </div>
    </footer>
  );
};
